/**
 * One symbol's financials, fetched on demand and cached per symbol.
 *
 * **Per symbol, not per view.** The PIP, the data panel and a detached window
 * can all be showing the same ticker at once, and each is a separate component
 * — so the cache is keyed on the request rather than held in a component, and
 * three hosts share one fetch. That is the same reason `symbol-index.ts` holds
 * its promise at module scope.
 *
 * **A failure is not cached.** `undefined` usually means qv-data is not up yet,
 * and a developer who starts it after the app should get a working panel on the
 * next open rather than after a reload — `symbol-index.ts` states the rule and
 * the reasoning transfers unchanged.
 *
 * The depth half is a separate cache key from the shallow one, because they are
 * different documents: asking for the compact form first and the full form
 * afterwards must not serve the compact answer to the panel.
 */

import { useEffect, useState } from "react";

import { fetchSymbolFundamentals, type SymbolFundamentals } from "../api/symbols";

const cache = new Map<string, Promise<SymbolFundamentals | undefined>>();

function key(symbol: string, depth: boolean): string {
  return `${symbol.toUpperCase()}|${depth ? "full" : "compact"}`;
}

/** The document, or `undefined` while loading and on failure. */
export function loadFundamentals(
  symbol: string,
  depth = true,
): Promise<SymbolFundamentals | undefined> {
  // No symbol, no request — see `symbol-profile.ts` for the same guard.
  if (!symbol) return Promise.resolve(undefined);
  const id = key(symbol, depth);
  const existing = cache.get(id);
  if (existing) return existing;

  const pending = fetchSymbolFundamentals(symbol, { depth }).then((result) => {
    // Only a *successful* answer is kept. See the module note.
    if (result === undefined) cache.delete(id);
    return result;
  });
  cache.set(id, pending);
  return pending;
}

/** Drop everything. For tests, and for a host that knows the data moved. */
export function invalidateFundamentals(): void {
  cache.clear();
}

/**
 * React binding. `undefined` while loading; a document once it lands.
 *
 * The symbol is captured per effect run and checked on resolution, so switching
 * ticker mid-flight cannot paint the previous symbol's numbers under the new
 * one's heading — which is the one bug a panel like this reliably ships with.
 */
export function useFundamentals(
  symbol: string,
  depth = true,
): SymbolFundamentals | undefined {
  const [data, setData] = useState<SymbolFundamentals | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setData(undefined);
    void loadFundamentals(symbol, depth).then((result) => {
      if (live) setData(result);
    });
    return () => {
      live = false;
    };
  }, [symbol, depth]);

  return data;
}
